const trustedTypesPolicy = window.trustedTypes?.createPolicy?.("offline-support-service-worker", {
    createScriptURL: value => value,
});
const deferNonCritical = callback => {
    if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(callback, {
            timeout: 2000,
        });
        return;
    }
    setTimeout(callback, 1);
};
const disallowImagesDownload = () => {
    const stopForImage = event => {
        if (event.target.tagName === "IMG") {
            event.preventDefault();
        }
    };

    document.addEventListener("mousedown", stopForImage, true);
    document.addEventListener("contextmenu", stopForImage, true);
};
const shareButton = element => {
    if (element) {
        const shareData = {
            title: document.title,
            text: document.querySelector("meta[name=description]")?.content,
            url: location.href,
        };

        if (navigator?.canShare?.(shareData)) {
            element.addEventListener("click", () =>
                navigator.share(shareData).catch(() => {
                    /**/
                }),
            );
        } else {
            element.hidden = true;
        }
    }
};
const verifyOnline = () =>
    fetch("/ping.txt", {
        method: "HEAD",
        cache: "no-store",
    })
        .then(() => true)
        .catch(() => false);

const sideMenu = () => {
    let isMenuExpanded = false;
    let focusOutTimeout;
    const menuContainer = document.getElementById("menu");
    const areasOutsideMenu = [
        document.getElementById("main"),
        document.getElementById("subnav"),
        document.getElementById("footer"),
    ].filter(Boolean);
    const toggleOutsideInteractivity = isBlocked => {
        areasOutsideMenu.forEach(outsideArea => (outsideArea.inert = isBlocked));
        const scrollbar = document.getElementById("scrollbar");
        if (scrollbar) {
            scrollbar.hidden = isBlocked;
        }
    };
    const expandMenu = event => {
        event.preventDefault();
        const expandClassName = "expand";
        menuContainer?.classList.toggle(expandClassName);
        isMenuExpanded = menuContainer?.classList.contains(expandClassName);
        [
            document.documentElement,
            document.body,
        ].forEach(el => el.classList.toggle("noscroll-onmobile"));
        toggleOutsideInteractivity(isMenuExpanded);

        if (event.target.id === "bar") {
            if (focusOutTimeout) {
                clearTimeout(focusOutTimeout);
            }

            event.target.setAttribute("aria-expanded", isMenuExpanded);
            focusOutTimeout = setTimeout(() => event.target.blur(), 300);
        }
    };
    const accessibilityOnScreenResize = (() => {
        let timer;

        return () => {
            if (timer) {
                cancelAnimationFrame(timer);
            }

            timer = requestAnimationFrame(() => {
                const isMobileUI = !!document.getElementById("bar")?.offsetParent;
                toggleOutsideInteractivity(isMobileUI && isMenuExpanded);
            });
        };
    })();

    [
        document.getElementById("bar"),
        document.getElementById("menu-outer"),
    ]
        .filter(Boolean)
        .forEach(menuHandler => menuHandler.addEventListener("click", expandMenu));

    addEventListener("resize", accessibilityOnScreenResize);
};

const offlineDetection = () => {
    const offlineEl = document.getElementById("offline-content");
    const onlineEl = document.getElementById("online-content");

    if (!offlineEl || !onlineEl) {
        return;
    }

    let debounceTimer;
    const debounceDelay = 2000;

    const showOffline = () => {
        offlineEl.hidden = false;
        onlineEl.hidden = true;
    };

    const showOnline = () => {
        offlineEl.hidden = true;
        onlineEl.hidden = false;
    };

    const handleOnline = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            verifyOnline().then(isOnline => isOnline && showOnline());
        }, debounceDelay);
    };

    const handleOffline = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(showOffline, debounceDelay);
    };

    addEventListener("online", handleOnline);
    addEventListener("offline", handleOffline);

    if (navigator.onLine) {
        verifyOnline().then(isOnline => isOnline && showOnline());
    }
};

const offlineImageReload = () => {
    const failedImages = {};
    const reloadedImages = {};
    let debounceTimer;
    const debounceDelay = 2000;

    const reloadImages = () => {
        Object.keys(failedImages).forEach(url => {
            if (reloadedImages[url]) {
                return;
            }
            document.querySelectorAll(`img[src="${url}"]`).forEach(img => {
                const src = img.src;
                img.src = "";
                img.src = src;
            });
            reloadedImages[url] = true;
            delete failedImages[url];
        });
    };

    const handleOnline = () => {
        if (!Object.keys(failedImages).length) {
            return;
        }
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            verifyOnline().then(isOnline => isOnline && reloadImages());
        }, debounceDelay);
    };

    navigator.serviceWorker.addEventListener("message", event => {
        event.data?.type === "fallback-image" && (failedImages[event.data.url] = true);
    });

    addEventListener("online", handleOnline);
};

const offlineSupport = () => {
    if (top === self) {
        const isFirstVisit = !navigator.serviceWorker.controller;
        const cacheResources = () => {
            if (!navigator.onLine) {
                return;
            }

            void Promise.allSettled(
                [
                    location.href,
                    "/css/style.css",
                    "/js/bundle.js",
                    "/manifest.webmanifest",
                    "/css/charter.woff2",
                    "/img/icon.svg",
                ].map(url => fetch(url)),
            );
        };
        navigator.serviceWorker.addEventListener("controllerchange", cacheResources);
        const serviceWorkerUrl = trustedTypesPolicy?.createScriptURL("/service-worker.mjs") ?? "/service-worker.mjs";
        void navigator.serviceWorker
            .register(serviceWorkerUrl, {
                type: "module",
            })
            .catch(error => window.FS?.log?.(error));
        if (isFirstVisit) {
            navigator.serviceWorker.ready.then(cacheResources);
        }
        offlineDetection();
        offlineImageReload();
    }
};

const overlayScrollbar = () => {
    const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine) and (not (any-pointer: coarse))");
    let scrollbar = null;
    let cleanup = null;

    function init() {
        if (scrollbar) {
            return;
        }

        let syncedScrollTop = 0;
        let scrollTimeout;
        let frameId;
        const page = document.scrollingElement;
        const wrapper = document.getElementById("wrapper");

        scrollbar = document.createElement("div");
        scrollbar.id = "scrollbar";
        scrollbar.className = "scrollbar";
        const thumb = document.createElement("div");
        thumb.className = "scrollbar-thumb";
        scrollbar.append(thumb);
        wrapper.append(scrollbar);

        function clampScrollTop(scrollTop) {
            const maxScroll = Math.max(0, page.scrollHeight - page.clientHeight);
            return Math.min(Math.max(0, scrollTop), maxScroll);
        }

        function setThumbHeight() {
            const height = `${page.scrollHeight}px`;
            thumb.animate(
                [
                    {
                        height,
                    },
                    {
                        height,
                    },
                ],
                {
                    duration: 1,
                    fill: "both",
                },
            );
            syncFromPage();
        }
        function syncFromPage() {
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }

            scrollbar.className = "scrollbar scrolling";
            scrollbar.scrollTop = clampScrollTop(page.scrollTop);
            syncedScrollTop = scrollbar.scrollTop;
            scrollTimeout = setTimeout(() => {
                if (scrollbar) {
                    scrollbar.className = "scrollbar";
                }
            }, 80);
        }
        function syncFromScrollbar() {
            if (scrollbar.scrollTop === syncedScrollTop) {
                return;
            }
            page.scrollTop = clampScrollTop(scrollbar.scrollTop);
        }

        scrollbar.addEventListener("scroll", syncFromScrollbar, {
            passive: true,
        });
        addEventListener("scroll", syncFromPage, {
            passive: true,
        });

        const ro = new ResizeObserver(setThumbHeight);
        ro.observe(wrapper);
        ro.observe(page);

        frameId = requestAnimationFrame(setThumbHeight);

        cleanup = () => {
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
                scrollTimeout = null;
            }
            if (frameId) {
                cancelAnimationFrame(frameId);
                frameId = null;
            }
            scrollbar?.remove();
            removeEventListener("scroll", syncFromPage);
            ro.disconnect();
            scrollbar = null;
            cleanup = null;
        };
    }

    function handleChange() {
        if (mediaQuery.matches) {
            init();
        } else if (cleanup) {
            cleanup();
        }
    }

    handleChange();
    mediaQuery.addEventListener("change", handleChange);
};

export {deferNonCritical, disallowImagesDownload, offlineSupport, overlayScrollbar, shareButton, sideMenu};
